import type { BootstrapConfig, BootstrapResult } from '../types';
import { isLocalDevHost } from '../utils/env';

/** Read an embed token from the URL hash (`#token=...`) or query string (`?token=...`). */
export function readTokenFromUrl(): string {
  if (typeof window === 'undefined') return '';
  const hash = String(window.location.hash || '').replace(/^#/, '');
  const hashParams = new URLSearchParams(hash);
  const fromHash = (hashParams.get('token') || '').trim();
  if (fromHash) return fromHash;
  const params = new URLSearchParams(window.location.search || '');
  return (params.get('token') || '').trim();
}

/**
 * Authenticate with the Bry3D backend.
 *
 * Reads the embed token from the URL, calls POST /functions/v1/bootstrap
 * with the `x-embed-token` header, and returns the bootstrap payload.
 *
 * On localhost without a token, authentication is bypassed (dev mode).
 *
 * @throws {{ code: string; reason: string }} on failure
 */
export async function bootstrap(config: BootstrapConfig): Promise<BootstrapResult> {
  const token = readTokenFromUrl();

  // Localhost dev bypass
  if (isLocalDevHost() && !token) {
    return { ok: true, devBypass: true } as BootstrapResult;
  }

  const supabaseUrl = (config.supabaseUrl || '').trim();
  if (!supabaseUrl) {
    const err: any = new Error('Supabase is not configured');
    err.code = 'BS_ENV_MISSING';
    err.reason = 'supabase_not_configured';
    throw err;
  }

  if (!token) {
    const err: any = new Error('Missing embed token');
    err.code = 'BS_TOKEN_MISSING';
    err.reason = 'missing_token';
    throw err;
  }

  const url = `${supabaseUrl.replace(/\/+$/, '')}/functions/v1/bootstrap`;
  const headers: Record<string, string> = {
    'content-type': 'application/json',
    'x-embed-token': token,
  };

  const anon = (config.supabaseAnonKey || '').trim();
  if (anon) {
    headers.apikey = anon;
    headers.authorization = `Bearer ${anon}`;
  }

  const res = await fetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify({}),
  });

  const json = await res.json().catch(() => null);
  if (!res.ok || !json || json.ok !== true) {
    const code = String(json?.code || '');
    const reason = String(json?.reason || `http_${res.status}`);
    const err: any = new Error(code || reason);
    err.code = code;
    err.reason = reason;
    throw err;
  }

  return json as BootstrapResult;
}
